import React, { useEffect } from 'react';
import { FileText, CreditCard, Shield, AlertTriangle, CheckCircle, Users, BookOpen, Home, Camera, UserCheck, Heart, Award } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Layout from '../../../components/web/Layout';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { CONTACT_ROUTE } from '../../../routes/web/rootRoutes';
const MembershipTerms = () => {
  useEffect(() => {
    AOS.init({
      duration: 600,
      once: true,
      easing: 'ease-out-cubic'
    });
  }, []);

  const documents = [
    {
      title: "Education / Tech. Qualification",
      description: "Degree | Diploma etc.",
      icon: BookOpen,
      color: "from-blue-500 to-cyan-600"
    },
    {
      title: "Residential Address Proof",
      description: "Ration card | Passport | Adhar Card (Any One)",
      icon: Home,
      color: "from-purple-500 to-indigo-600"
    },
    {
      title: "Photo ID",
      description: "PAN Card | Driving License etc. (Any one)",
      icon: Award,
      color: "from-emerald-500 to-teal-600"
    },
    {
      title: "Recent Photographs",
      description: "2 Nos. (Passport Size)",
      icon: Camera,
      color: "from-pink-500 to-rose-600"
    },
    {
      title: "Reference Letter",
      description: "From 3 Art Direction (must be the members of our Association)",
      icon: UserCheck,
      color: "from-amber-500 to-orange-600"
    },
    {
      title: "Health Certificate",
      description: "Valid medical fitness certificate",
      icon: Heart,
      color: "from-red-500 to-pink-600"
    }
  ];

  const terms = [
    {
      text: "Once the applicant for membership has been approved by the executive committee, the applicant will have to comply with all formalities relating to payment within one month.",
      icon: CheckCircle,
      highlight: "one month"
    },
    {
      text: "Amount paid by applicant is non-refundable.",
      icon: AlertTriangle,
      highlight: "non-refundable"
    },
    {
      text: "In case the applicant utilizes the installment facility, he/she must submit all PDC along with the application.",
      icon: CreditCard,
      highlight: "PDC"
    },
    {
      text: "PDC (Post Dated Cheque) submitted by applicants should be duly honored. The application will be treated cancelled in case of default in realization of any PDC submitted by applicant for more than two months. In case the amount paid by the applicant will be forfeited and transferred to welfare fund of ACTADCD. The applicant will have to submit a fresh application.",
      icon: Shield,
      highlight: "two months"
    },
    {
      text: "ACTADCD will not entertain any dispute matter of the applicant till such time he/she gets membership number. The applicant will not be entitled to right to vote till he/she is given the membership number.",
      icon: Users,
      highlight: "membership number"
    },
    {
      text: "Only when all payment have been made, the applicant will get the membership card and enjoy all the benefits available to members of ACTADCD.",
      icon: CheckCircle,
      highlight: "all payment"
    },
    {
      text: "All payments should be made in favor of ACTADCD.",
      icon: CreditCard,
      highlight: "ACTADCD"
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Membership terms</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="relative container mx-auto px-4 py-20 text-center">
            <div data-aos="zoom-in">
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full filter blur-xl opacity-50 animate-pulse"></div>
                  <div className="relative bg-white/10 backdrop-blur-lg rounded-full p-6 border-2 border-white/30">
                    <FileText className="w-16 h-16 text-white" />
                  </div>
                </div>
              </div>
              <h1 className="text-6xl md:text-7xl font-black mb-4 tracking-tight">
                MEMBERSHIP TERMS
              </h1>
              <div className="w-40 h-1.5 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mx-auto rounded-full mb-6"></div>
              <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto font-light">
                Join the premier association for cinema and television professionals
              </p>
            </div>
          </div>
        </div>

        {/* Documents Required Section */}
        <div className="container mx-auto px-4 py-16">
          <div data-aos="fade-up">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
                Documents Required
              </h2>
              <p className="text-xl text-gray-600">Please prepare the following documents for your application</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.map((doc, index) => {
                const IconComponent = doc.icon;
                return (
                  <div
                    key={index}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                    className="group"
                  >
                    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full transform hover:-translate-y-2">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${doc.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {doc.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {doc.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Terms and Conditions Section */}
        <div className="container mx-auto px-4 py-16">
          <div data-aos="fade-up">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-8 md:p-12 mb-8 shadow-2xl">
              <div className="flex items-center gap-4 justify-center">
                <Shield className="w-12 h-12 text-white" />
                <h2 className="text-4xl md:text-5xl font-black text-white text-center">
                  Terms and Conditions
                </h2>
              </div>
            </div>

            <div className="space-y-4">
              {terms.map((term, index) => {
                const IconComponent = term.icon;
                return (
                  <div
                    key={index}
                    data-aos="fade-right"
                    data-aos-delay={index * 50}
                    className="group"
                  >
                    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden">
                      <div className="flex gap-4 p-6">
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full filter blur-md opacity-50 group-hover:opacity-75 transition-opacity"></div>
                            <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                              <span className="text-white font-bold text-lg">{index + 1}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-700 leading-relaxed text-base">
                            {term.text}
                          </p>
                        </div>
                        <div className="flex-shrink-0 self-start">
                          <IconComponent className="w-6 h-6 text-indigo-500 opacity-50 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      <div className="h-1 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="container mx-auto px-4 pb-16">
          <div data-aos="fade-up">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 shadow-2xl text-white">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="bg-white/20 backdrop-blur-lg rounded-full p-3">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Important Notice</h3>
                  <p className="text-white/95 leading-relaxed text-lg">
                    Please ensure all documents are valid and authentic. Incomplete applications or fraudulent documents will result in immediate rejection. All payments should be made in favor of <span className="font-bold">ACTADCD</span> only.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 pb-16">
          <div data-aos="zoom-in">
            <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 rounded-3xl p-12 text-center shadow-2xl">
              <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
                Ready to Join?
              </h3>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Become a member of ACTADCD and be part of India's premier association for cinema and television art directors and costume designers
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
             
                <Link to={CONTACT_ROUTE} className="bg-white/10 backdrop-blur-lg border-2 border-white/30 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/20 transition-colors shadow-lg transform hover:scale-105 duration-300">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>


      </div>
    </Layout>
  );
};

export default MembershipTerms;